/**
 * Bank Transaction Attribution diagnostic card.
 *
 * Per bank-feed source in db.transactions: entity_id fill rate (split into
 * missing vs. stored-as-slug vs. stored-as-UUID -- the slug/UUID split is
 * what actually explains "no entity shown" on the Transactions tab, since
 * every entityById lookup this session found indexes by UUID) and
 * counterparty fill rate with a per-source verdict on whether a real live
 * payload was inspected before proposing a fix (never guess a field name).
 */

import { useState } from 'react';
import { Loader2, ShieldCheck, ShieldAlert, CheckCircle2, XCircle, HelpCircle, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';
import { RevolutCounterpartyBackfillSection } from './RevolutCounterpartyBackfillSection';
import { EbCounterpartyBackfillSection } from './EbCounterpartyBackfillSection';

// Sources this card knows how to fetch a live sample for. Others have no
// sampler implemented yet (see admin_bank_transaction_attribution.py
// sample_live_payload) -- the button is only rendered for these.
var LIVE_SAMPLE_SOURCES = new Set(['revolut', 'enablebanking', 'wise']);

function VerdictBadge({ verdict, verified }) {
  if (verified) {
    return (
      <span className="inline-flex items-center gap-1 text-green-700" data-testid="counterparty-verdict-verified">
        <CheckCircle2 className="h-3 w-3" />
        <span className="text-xs">{verdict}</span>
      </span>
    );
  }
  if (verdict === 'no-live-connection-to-verify') {
    return (
      <span className="inline-flex items-center gap-1 text-amber-700" data-testid="counterparty-verdict-unverified">
        <XCircle className="h-3 w-3" />
        <span className="text-xs">{verdict}</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-muted-foreground" data-testid="counterparty-verdict-unverified">
      <HelpCircle className="h-3 w-3" />
      <span className="text-xs">{verdict}</span>
    </span>
  );
}

// Three-state field verdict (CHANGE 4): parsed / genuinely_absent_in_feed /
// not_yet_verified. A bank with no live payload inspected this session
// always shows not_yet_verified, never reads as absent.
var FIELD_VERDICT_META = {
  parsed: { label: 'Parsed', className: 'text-green-700 border-green-300' },
  genuinely_absent_in_feed: { label: 'Genuinely absent in feed', className: 'text-amber-700 border-amber-300' },
  not_yet_verified: { label: 'Not yet verified', className: 'text-muted-foreground border-border' },
};

function FieldVerdictBadge({ fieldVerdict }) {
  var meta = FIELD_VERDICT_META[fieldVerdict] || FIELD_VERDICT_META.not_yet_verified;
  return (
    <Badge variant="outline" className={meta.className} data-testid={'field-verdict-' + fieldVerdict}>
      {meta.label}
    </Badge>
  );
}

function fillRatePct(field) {
  if (!field || field.fill_rate == null) return '—';
  return Math.round(field.fill_rate * 100) + '%';
}

function EntityBar({ entity }) {
  var total = entity.missing + entity.resolved_as_uuid + entity.resolved_as_slug + entity.orphan_unresolvable_value;
  if (total === 0) return null;
  var pct = function (n) { return (100 * n / total).toFixed(0) + '%'; };
  return (
    <div className="flex h-2 w-full rounded overflow-hidden bg-muted" title={JSON.stringify(entity)}>
      {entity.missing > 0 && <div className="bg-red-500" style={{ width: pct(entity.missing) }} />}
      {entity.resolved_as_slug > 0 && <div className="bg-amber-500" style={{ width: pct(entity.resolved_as_slug) }} />}
      {entity.orphan_unresolvable_value > 0 && <div className="bg-purple-500" style={{ width: pct(entity.orphan_unresolvable_value) }} />}
      {entity.resolved_as_uuid > 0 && <div className="bg-green-500" style={{ width: pct(entity.resolved_as_uuid) }} />}
    </div>
  );
}

export function BankTransactionAttributionPanel() {
  var [loading, setLoading] = useState(false);
  var [data, setData] = useState(null);
  // Per-source live-sample state: { [source]: { loading, result } }
  var [samples, setSamples] = useState({});

  var run = async function () {
    setLoading(true);
    setData(null);
    try {
      var res = await diagnosticsAPI.bankTransactionAttribution();
      setData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  var sampleLivePayload = async function (source) {
    setSamples(function (prev) {
      return Object.assign({}, prev, { [source]: { loading: true, result: prev[source] && prev[source].result } });
    });
    try {
      var res = await diagnosticsAPI.bankTransactionAttributionSampleLivePayload(source);
      setSamples(function (prev) {
        return Object.assign({}, prev, { [source]: { loading: false, result: res.data } });
      });
    } catch (err) {
      toast.error(getErrorMessage(err));
      setSamples(function (prev) {
        return Object.assign({}, prev, { [source]: { loading: false, result: { available: false, reason: getErrorMessage(err) } } });
      });
    }
  };

  return (
    <Card data-testid="bank-transaction-attribution-panel">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          {data ? (
            data.summary.total_missing_entity_id === 0 && data.summary.total_entity_id_stored_but_not_uuid === 0 ? (
              <ShieldCheck className="h-5 w-5 text-green-600" />
            ) : (
              <ShieldAlert className="h-5 w-5 text-amber-600" />
            )
          ) : (
            <ShieldCheck className="h-5 w-5 text-muted-foreground" />
          )}
          Bank Transaction Attribution
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Per bank-feed source: entity_id and counterparty fill rate, plus
          whether a real live payload was inspected before proposing a
          counterparty-parsing fix for that source.
        </p>

        <Button onClick={run} disabled={loading} data-testid="run-attribution-diagnostic">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
          Run Diagnostic
        </Button>

        <div className="space-y-3">
          <RevolutCounterpartyBackfillSection />
          <EbCounterpartyBackfillSection />
        </div>

        {data && (
          <div className="space-y-4">
            <div className="rounded-md px-3 py-2 text-sm bg-muted/50 border">
              {data.summary.headline}
            </div>

            <div className="flex gap-2 flex-wrap">
              <Badge variant="outline">{data.summary.total_transactions} transactions</Badge>
              <Badge variant="destructive">{data.summary.total_missing_entity_id} missing entity_id</Badge>
              <Badge variant="outline" className="text-amber-700 border-amber-300">
                {data.summary.total_entity_id_stored_but_not_uuid} stored but not UUID (display will fail)
              </Badge>
              <Badge variant="outline" className="text-amber-700 border-amber-300">
                {data.summary.total_null_counterparty} null counterparty
              </Badge>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="attribution-table">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-1.5 pr-3 font-medium">Source</th>
                    <th className="py-1.5 pr-3 font-medium">Total</th>
                    <th className="py-1.5 pr-3 font-medium">Entity_id fill</th>
                    <th className="py-1.5 pr-3 font-medium">Counterparty fill</th>
                    <th className="py-1.5 pr-3 font-medium">Reference fill</th>
                    <th className="py-1.5 pr-3 font-medium">Verdict</th>
                    <th className="py-1.5 pr-3 font-medium">Counterparty verdict detail</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sources.map(function (s) {
                    return (
                      <tr key={s.source} className="border-b border-border/50 align-top">
                        <td className="py-1.5 pr-3 font-mono text-xs">{s.source}</td>
                        <td className="py-1.5 pr-3">{s.total_transactions}</td>
                        <td className="py-1.5 pr-3 w-40">
                          <EntityBar entity={s.entity_id} />
                          <div className="text-[10px] text-muted-foreground mt-1">
                            {s.entity_id.missing} missing · {s.entity_id.resolved_as_slug} slug · {s.entity_id.resolved_as_uuid} uuid
                            {s.entity_id.orphan_unresolvable_value > 0 ? ' · ' + s.entity_id.orphan_unresolvable_value + ' orphan' : ''}
                          </div>
                        </td>
                        <td className="py-1.5 pr-3" data-testid="counterparty-fill-rate">
                          {fillRatePct(s.counterparty)}
                          <div className="text-[10px] text-muted-foreground">
                            {s.counterparty.populated} / {s.counterparty.populated + s.counterparty.null_or_empty}
                          </div>
                        </td>
                        <td className="py-1.5 pr-3" data-testid="reference-fill-rate">
                          {fillRatePct(s.reference)}
                          <div className="text-[10px] text-muted-foreground">
                            {s.reference.populated} / {s.reference.populated + s.reference.null_or_empty}
                          </div>
                        </td>
                        <td className="py-1.5 pr-3">
                          <FieldVerdictBadge fieldVerdict={s.field_verdict} />
                        </td>
                        <td className="py-1.5 pr-3 max-w-md">
                          <VerdictBadge
                            verdict={s.counterparty_verdict.verdict}
                            verified={s.counterparty_verdict.verified_live_this_session}
                          />
                          <div className="text-[11px] text-muted-foreground mt-1">
                            {s.counterparty_verdict.detail}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {data.sources.some(function (s) { return s.missing_entity_sample.length > 0; }) && (
              <div>
                <div className="text-xs font-medium mb-1">Missing-entity sample (derivability)</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="missing-entity-sample-table">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-1.5 pr-3 font-medium">Source</th>
                        <th className="py-1.5 pr-3 font-medium">Transaction</th>
                        <th className="py-1.5 pr-3 font-medium">Bank account</th>
                        <th className="py-1.5 pr-3 font-medium">Classification</th>
                        <th className="py-1.5 pr-3 font-medium">Account entity_id</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.sources.flatMap(function (s) {
                        return s.missing_entity_sample.map(function (row, i) {
                          return (
                            <tr key={s.source + i} className="border-b border-border/50">
                              <td className="py-1.5 pr-3 font-mono text-xs">{s.source}</td>
                              <td className="py-1.5 pr-3 font-mono text-xs">{row.transaction_id}</td>
                              <td className="py-1.5 pr-3 font-mono text-xs">{row.bank_account_id || '—'}</td>
                              <td className="py-1.5 pr-3">
                                <Badge
                                  variant="outline"
                                  className={
                                    row.classification === 'derivable'
                                      ? 'text-green-700 border-green-300'
                                      : 'text-muted-foreground'
                                  }
                                >
                                  {row.classification}
                                </Badge>
                              </td>
                              <td className="py-1.5 pr-3 font-mono text-xs">{row.account_entity_id || '—'}</td>
                            </tr>
                          );
                        });
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div>
              <div className="text-xs font-medium mb-1">Stored-raw census</div>
              <p className="text-[11px] text-muted-foreground mb-1">
                For sources that persist the provider's raw payload on the transaction doc: what
                fraction of stored rows actually carry one, and whether any surviving key looks
                counterparty-adjacent. Sources with no stored raw payload are flagged not applicable
                rather than reporting a fabricated 0%.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="stored-raw-census-table">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-1.5 pr-3 font-medium">Source</th>
                      <th className="py-1.5 pr-3 font-medium">Applicable</th>
                      <th className="py-1.5 pr-3 font-medium">Raw coverage</th>
                      <th className="py-1.5 pr-3 font-medium">Counterparty-adjacent keys found</th>
                      <th className="py-1.5 pr-3 font-medium">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.sources.map(function (s) {
                      var c = s.stored_raw_census || {};
                      return (
                        <tr key={s.source} className="border-b border-border/50 align-top">
                          <td className="py-1.5 pr-3 font-mono text-xs">{s.source}</td>
                          <td className="py-1.5 pr-3">
                            {c.applicable ? (
                              <Badge variant="outline" className="text-green-700 border-green-300">yes</Badge>
                            ) : (
                              <Badge variant="outline" className="text-muted-foreground">n/a</Badge>
                            )}
                          </td>
                          <td className="py-1.5 pr-3 font-mono text-xs">
                            {c.applicable
                              ? (c.raw_coverage_fill_rate == null ? '—' : Math.round(c.raw_coverage_fill_rate * 100) + '%') +
                                ' (' + c.with_stored_raw + ' / ' + c.total_transactions + ')'
                              : '—'}
                          </td>
                          <td className="py-1.5 pr-3 font-mono text-xs">
                            {c.applicable && c.counterparty_adjacent_keys_found && c.counterparty_adjacent_keys_found.length > 0
                              ? c.counterparty_adjacent_keys_found.join(', ')
                              : '—'}
                          </td>
                          <td className="py-1.5 pr-3 text-[11px] text-muted-foreground max-w-md">{c.note}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <div className="text-xs font-medium mb-1">Parser coverage</div>
              <p className="text-[11px] text-muted-foreground mb-1">
                For sources with a tagged counterparty parser: how many transactions parsed
                successfully vs. matched no known grammar (null-no-pattern, a visible gap -- not
                the same as a genuinely empty remittance line, null-empty). Sources with no tagged
                parser are flagged not applicable.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="parser-coverage-table">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-1.5 pr-3 font-medium">Source</th>
                      <th className="py-1.5 pr-3 font-medium">Applicable</th>
                      <th className="py-1.5 pr-3 font-medium">Parsed</th>
                      <th className="py-1.5 pr-3 font-medium">Null (no pattern)</th>
                      <th className="py-1.5 pr-3 font-medium">Null (empty)</th>
                      <th className="py-1.5 pr-3 font-medium">By tag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.sources.map(function (s) {
                      var pc = s.parser_coverage;
                      return (
                        <tr key={s.source} className="border-b border-border/50 align-top">
                          <td className="py-1.5 pr-3 font-mono text-xs">{s.source}</td>
                          <td className="py-1.5 pr-3">
                            {pc ? (
                              <Badge variant="outline" className="text-green-700 border-green-300">yes</Badge>
                            ) : (
                              <Badge variant="outline" className="text-muted-foreground">n/a</Badge>
                            )}
                          </td>
                          <td className="py-1.5 pr-3 font-mono text-xs">
                            {pc ? pc.parsed + ' / ' + pc.total : '—'}
                          </td>
                          <td className="py-1.5 pr-3 font-mono text-xs">{pc ? pc.null_no_pattern : '—'}</td>
                          <td className="py-1.5 pr-3 font-mono text-xs">{pc ? pc.null_empty : '—'}</td>
                          <td className="py-1.5 pr-3 text-[11px] text-muted-foreground max-w-md">
                            {pc ? Object.keys(pc.by_tag).map(function (tag) {
                              return tag + ': ' + pc.by_tag[tag];
                            }).join(', ') : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <div className="text-xs font-medium mb-1">Sample live payload</div>
              <p className="text-[11px] text-muted-foreground mb-2">
                Fetch up to 10 recent transactions' raw payloads from a live connection for a
                source with a sampler implemented, and show a per-path fill rate across the
                sample so one unrepresentative transaction can't mislead. Shows "not available"
                rather than a guessed payload when this environment has no connection.
              </p>
              <div className="space-y-3">
                {data.sources.filter(function (s) { return LIVE_SAMPLE_SOURCES.has(s.source); }).map(function (s) {
                  var st = samples[s.source] || {};
                  return (
                    <div key={s.source} className="border rounded-md p-2" data-testid={'sample-live-payload-' + s.source}>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs">{s.source}</span>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={st.loading}
                          onClick={function () { sampleLivePayload(s.source); }}
                          data-testid={'sample-live-payload-btn-' + s.source}
                        >
                          {st.loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Download className="h-3 w-3 mr-1" />}
                          Sample Live Payload
                        </Button>
                      </div>
                      {st.result && (
                        st.result.available ? (
                          <div className="mt-2 overflow-x-auto">
                            <p className="text-[10px] text-muted-foreground mb-1">
                              Sampled {st.result.sample_size_actual} of {st.result.sample_size_requested} requested recent transaction(s).
                            </p>
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b text-left">
                                  <th className="py-1 pr-3 font-medium">Path</th>
                                  <th className="py-1 pr-3 font-medium">Type</th>
                                  <th className="py-1 pr-3 font-medium">Non-null</th>
                                  <th className="py-1 pr-3 font-medium">Example value</th>
                                </tr>
                              </thead>
                              <tbody>
                                {st.result.path_census.map(function (kp, i) {
                                  return (
                                    <tr
                                      key={i}
                                      className={'border-b border-border/50 ' + (kp.counterparty_relevant ? 'bg-amber-50' : '')}
                                    >
                                      <td className="py-1 pr-3 font-mono text-xs">{kp.path}</td>
                                      <td className="py-1 pr-3 text-xs">{kp.type}</td>
                                      <td className="py-1 pr-3 font-mono text-xs">
                                        {kp.non_null_count} / {kp.sample_size}
                                        {kp.fill_rate == null ? '' : ' (' + Math.round(kp.fill_rate * 100) + '%)'}
                                      </td>
                                      <td className="py-1 pr-3 font-mono text-xs">{kp.example_value == null ? '—' : kp.example_value}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="mt-2 text-xs text-amber-700" data-testid={'sample-live-payload-unavailable-' + s.source}>
                            Not available: {st.result.reason}
                          </div>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
